// Display rules lifted from the Price Ops design handoff (Claude Design,
// .context/attachments/d8oCII): number formatting, quote freshness and the
// live %-change pill shown while editing a cell.

const DAY_MS = 24 * 60 * 60 * 1000;
export const AMBER_DAYS = 4;
export const RED_DAYS = 8;
// The handoff warns on any move of at least ±25% ("Suspicious-jump warning").
export const JUMP_WARN_PERCENT = 25;

export function formatPrice(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export interface Freshness {
  label: string;
  color: string;
}

// Whole days since a timestamp, clamped at 0. Null for a missing/invalid
// timestamp — an unknown age is not "0 days old".
export function daysSince(timestamp: string | null, now: number = Date.now()): number | null {
  const time = timestamp ? new Date(timestamp).getTime() : NaN;
  if (Number.isNaN(time)) return null;
  return Math.max(0, Math.floor((now - time) / DAY_MS));
}

export function freshness(lastModifiedAt: string | null, now: number = Date.now()): Freshness | null {
  const days = daysSince(lastModifiedAt, now);
  if (days == null) return null;

  const label = days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days}d ago`;
  const color = days >= RED_DAYS ? '#C0392B' : days >= AMBER_DAYS ? '#B26B00' : '#9BA0AE';

  return { label, color };
}

export interface PctInfo {
  label: string;
  color: string;
  bg: string;
  warn: boolean;
}

// Change of `next` vs `current`: red pill going up, green going down; decimals
// drop once the move reaches ±10%; warn flag at the suspicious-jump threshold.
export function pctInfo(next: number, current: number | null): PctInfo | null {
  if (current == null || Number.isNaN(next) || current === 0) return null;

  const value = ((next - current) / current) * 100;
  const up = value > 0;
  const label = `${up ? '+' : ''}${value.toFixed(Math.abs(value) >= 10 ? 0 : 1)}%`;

  return {
    label,
    color: up ? '#C0392B' : '#157F4A',
    bg: up ? '#FBEAEA' : '#EAF5EE',
    warn: Math.abs(value) >= JUMP_WARN_PERCENT,
  };
}
